import { useCallback } from 'react';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { Channel } from 'shared/types/channel';
import { PaginatedResult } from 'shared/types/result';
import useSWR from 'swr';
import { sdk } from 'sdk';

export interface UseChannelReturn {
  channels: Channel[];
  getAllChannels: () => Promise<PaginatedResult<Channel>>;
  getChannelByID: (id: string) => Promise<Channel>;
  getChannelByKey: (key: string) => Promise<Channel>;
  isLoading: boolean;
  error: Error | null;
  mutate: () => void;
}

const useChannel = (): UseChannelReturn => {
  const { data: channelsData, error, isLoading, mutate } = useSWR<SDKResponse<PaginatedResult<Channel>>>(
    '/action/channel/getAllChannels',
    () =>
      sdk.callAction<PaginatedResult<Channel>>({
        actionName: 'channel/getAllChannels',
      }),
  );

  const channels = (channelsData?.isError ? [] : (channelsData?.data?.items as Channel[])) ?? [];

  const getAllChannels = useCallback(async () => {
    const res = await sdk.callAction<PaginatedResult<Channel>>({
      actionName: 'channel/getAllChannels',
    });
    if (res.isError) {
      throw new Error(res.error.message);
    }
    return res.data;
  }, []);

  const getChannelByID = useCallback(async (id: string) => {
    const res = await sdk.callAction<Channel>({
      actionName: 'channel/getChannelByID',
      query: { id },
    });
    if (res.isError) {
      throw new Error(res.error.message);
    }
    return res.data;
  }, []);

  const getChannelByKey = useCallback(async (key: string) => {
    const res = await sdk.callAction<Channel>({
      actionName: 'channel/getChannelByKey',
      query: { key },
    });
    if (res.isError) {
      throw new Error(res.error.message);
    }
    return res.data;
  }, []);

  return {
    channels,
    getAllChannels,
    getChannelByID,
    getChannelByKey,
    isLoading,
    error,
    mutate,
  };
};

export default useChannel;
